import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
  untracked,
} from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
  Scroll,
} from "@angular/router";
import { ToastModule } from "primeng/toast";
import { AnalyticsService } from "@platform-app/app/core/analytics/analytics.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RoutingUtility } from "@platform-app/app/shared/common/utilities";
import { PageTrackingConfig } from "@platform-app/app/core/analytics/models";
import { ScreenNotificationComponent } from "@platform-app/app/main/layout/screen-notification/screen-notification.component";
import { filter, map, pairwise, startWith, switchMap, tap } from "rxjs";
import { ViewportScroller } from "@angular/common";
import { DeviceSupportService } from "@platform-app/app/shared/common/services/device-support.service";
import { A2HSNotificationComponent } from "@platform-app/app/main/layout/a2hs-notification/a2hs-notification.component";
import { AuthState } from "@platform-app/app/core/auth/auth.state";
import { select, Store } from "@ngxs/store";
import { UserGroup } from "@platform-app/app/core/api/models";
import { SecureSessionDialogComponent } from "./main/layout/secure-session-dialog/secure-session-dialog.component";
import {
  PlacementsService,
  StudentService,
} from "@platform-app/app/core/api/services";
import { SignOut } from "@platform-app/app/core/auth/auth.actions";
import { InactiveStudentSessionService } from "@platform-app/app/core/auth/inactive-student-session.service";
import { UIPreferencesState } from "@platform-app/app/shared/common/store/preferences/ui-preferences.state";
import { MarkSecureSessionDialogSeen } from "@platform-app/app/shared/common/store/preferences/ui-preferences.actions";
import { ScreenWidthService } from "@platform-app/app/shared/common/services/screen-width.service";
import { PrimeNGConfigService } from "@platform-app/app/shared/common/services/primeng-config.service";
import { OnboardingWizardExitNavigationState } from "@platform-app/app/onboarding-wizard/shared/models";
import { RoutingService } from "@platform-app/app/shared/common/services";
import { IntroductionDialogComponent } from "@platform-app/app/main/dashboard-page/introduction-dialog/introduction-dialog.component";
import { AuthSyncService } from "@platform-app/app/core/auth/auth-sync.service";
import { HttpContext } from "@angular/common/http";
import { SUPPRESS_ERROR_MESSAGE } from "@platform-app/app/core/errors/interceptors";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    RouterOutlet,
    ToastModule,
    ScreenNotificationComponent,
    A2HSNotificationComponent,
    SecureSessionDialogComponent,
    IntroductionDialogComponent,
  ],
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly deviceSupportService = inject(DeviceSupportService);
  private readonly store = inject(Store);
  private readonly placementsService = inject(PlacementsService);
  private readonly studentService = inject(StudentService);
  private readonly screenWidthService = inject(ScreenWidthService);
  private readonly primeNgConfigService = inject(PrimeNGConfigService);
  private readonly routingService = inject(RoutingService);
  private readonly authSyncService = inject(AuthSyncService);
  private readonly inactiveStudentSessionService = inject(
    InactiveStudentSessionService,
  );

  currentUser = select(AuthState.user);
  isSecureSessionDialogSeen = select(
    UIPreferencesState.isSecureSessionDialogSeen,
  );

  isMobile = this.screenWidthService.isMobile;
  secureSessionDialogVisible = signal<boolean>(false);
  introductionDialogVisible = signal(false);

  isStudentUser = computed(
    () => this.currentUser()?.roleGroup === UserGroup.Student,
  );
  isInactiveStudent = select(AuthState.isInactiveStudent);
  currentUserId = computed(() => this.currentUser()?.id);

  showSecureSessionDialogEffect = effect(
    () => {
      const userId = this.currentUserId();
      const isStudent = this.isStudentUser();
      const isInactive = this.isInactiveStudent();
      const isSecureSessionDialogSeen = untracked(
        this.isSecureSessionDialogSeen,
      );

      if (!userId || !isStudent || isInactive || isSecureSessionDialogSeen)
        return;

      this.showSecureSessionDialog(userId);
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.deviceSupportService.initializeDeviceSupportHandling();
    this.primeNgConfigService.overridePrimeNGConfig();
    this.initializeRoutingEvents();
    this.inactiveStudentSessionService.watch();
  }

  ngOnInit() {
    this.subscribeForPageTracking();
    this.handleScrollOnNavigation();
    this.authSyncService.subscribeToAuthUpdates();
  }

  @HostListener("window:unload", [])
  forceLogOut() {
    const secureSessionActive = this.store.selectSnapshot(
      AuthState.secureSessionActive,
    );

    if (secureSessionActive) this.store.dispatch(new SignOut());
  }

  private subscribeForPageTracking() {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const route = RoutingUtility.getDeepestRoute(this.activatedRoute);

          const trackingConfig = route?.snapshot.data[
            "tracking"
          ] as PageTrackingConfig;

          if (!trackingConfig) return;

          const { page, trackQueryParams } = trackingConfig;

          let params: Record<string, unknown> = {};
          if (trackQueryParams) {
            params =
              trackQueryParams instanceof Array
                ? Object.fromEntries(
                    Object.entries(route.snapshot.params).filter(([k]) =>
                      trackQueryParams.includes(k),
                    ),
                  )
                : route.snapshot.params;
          }

          if (page)
            this.analyticsService.trackPageView({
              page,
              ...params,
            });
        }
      });
  }

  private handleScrollOnNavigation(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof Scroll),
        startWith(null as Scroll | null),
        pairwise(),
        filter(([_, current]) => current !== null),
      )
      .subscribe(([previous, current]) => {
        if (current?.position) {
          // Restore scroll position on backward or forward navigation
          this.viewportScroller.scrollToPosition(current?.position);
        } else if (current?.anchor) {
          // Scroll to anchor on fragment navigation
          setTimeout(() => {
            const anchor = document.getElementById(current.anchor!);
            if (anchor) {
              anchor.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
              });
            }
          }, 100); // Minimal delay to let the DOM stabilize
        } else if (
          previous &&
          current &&
          RoutingUtility.getBaseRoute(previous?.routerEvent.url) !==
            RoutingUtility.getBaseRoute(current?.routerEvent.url)
        ) {
          // Scroll to top on actual base route change (excluding query/fragment)
          this.viewportScroller.scrollToPosition([0, 0]);
        }
      });
  }

  private showSecureSessionDialog(userId: string): void {
    this.studentService
      .getSecureSessionDialogStatus(
        { id: userId },
        new HttpContext().set(SUPPRESS_ERROR_MESSAGE, true),
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((res) => !res.isHidden),
        switchMap(() =>
          this.placementsService
            .getLatestIncompletePlacementId(
              undefined,
              new HttpContext().set(SUPPRESS_ERROR_MESSAGE, true),
            )
            .pipe(
              tap((res) => {
                if (res.incompletePlacementId && !this.isMobile()) {
                  this.secureSessionDialogVisible.set(true);
                  this.store.dispatch(new MarkSecureSessionDialogSeen());
                }
              }),
            ),
        ),
      )
      .subscribe();
  }

  private initializeRoutingEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(
          () =>
            this.router.getCurrentNavigation()?.extras
              .state as OnboardingWizardExitNavigationState,
        ),
        filter(
          (state): state is OnboardingWizardExitNavigationState =>
            !!state?.introductionDialogVisible,
        ),
      )
      .subscribe(() => {
        this.introductionDialogVisible.set(true);
        this.routingService.resetRouterState();
      });
  }
}
